// src/App.js
import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import "./App.css";

// 🧩 Contexts
import { AuthProvider } from "./contexts/AuthContext";
import { SellerProvider, useSeller } from "./contexts/SellerContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { SafeAreaProvider } from "./utils/SafeAreaProvider";
import { NavigationBarManager } from "./utils/NavigationBarManager";

// 📄 Pages
import Login from "./pages/Login";
import Register from "./pages/Register";
import SellerRegistration from "./pages/SellerRegistartion"; // check spelling of the file
import SegmentRegistration from "./pages/SegmentRegistration";
import ProductRegistration from "./pages/ProductRegistration";
import SellerProfile from "./pages/SellerProfile";
import Dashboard from "./components/Dashboard";
import UploadProduct from "./pages/UploadProduct";
import MyRegisteredProducts from "./pages/MyregisteredProducts";
import QCApprovalPage from "./pages/QCApprovalPage";
import ReadyStockServices from "./pages/ReadyStockServices";
import Catalogue from "./pages/Catalogue";
import BuyerRequest from "./pages/BuyerRequest";
import Assortment from "./pages/Assortment";
import FinalCorrection from "./pages/FinalCorrection";
import ReadyToDispatch from "./pages/ReadyToDispatch";
import Pickup from "./pages/Pickup";
import LiveRatesPage from "./pages/LiveRatesPage";
import ForgotPassword from "./pages/ForgetPassword";
import ProductDetails from "./pages/ProductDetails";
import Payment from "./pages/Payment";
import MyCatalogue from "./pages/MyCatalogue";
import OrderServe from "./pages/OrderServe";
import Preparation from "./pages/Preparation";
import Processing from "./pages/Processing";
import Openmarket from "./pages/Openmarket";
import SellerRewards from "./components/Rewards/SellerRewards";
import SellerOrderHistory from "./components/SellerOrderHistory";
import DashboardDebug from "./components/DashboardDebug";
import { getNextRoute, getFirstMissingStep } from "./utils/registrationFlow";

// 🧱 Registration flow helpers (see src/utils/registrationFlow.js)
const Splash = () => <div className="splash-screen">Loading...</div>;

// 🧱 Protected Route (dashboard + every functional page)
// Requires a logged-in seller AND a fully completed registration, so a seller
// can never reach the dashboard while a registration step is pending.
const ProtectedRoute = ({ children }) => {
  const { seller, loading } = useSeller();

  if (loading) return <Splash />;

  const nextRoute = getNextRoute(seller);
  if (nextRoute) return <Navigate to={nextRoute} replace />;

  return children;
};

// 🧱 Registration Step Route
// Keeps the step-by-step flow ordered: you must be logged in, and the earlier
// steps must already be finished before a later step can be opened.
const RegistrationRoute = ({ children, requires = [] }) => {
  const { seller, loading } = useSeller();

  if (loading) return <Splash />;

  if (!seller?.sellerId) return <Navigate to="/login" replace />;

  const missingStep = getFirstMissingStep(seller, requires);
  if (missingStep) return <Navigate to={missingStep.path} replace />;

  return children;
};

// 🧱 Public Route
const PublicRoute = ({ children }) => {
  const { seller, loading } = useSeller();
  console.log(seller);
  if (loading) return <Splash />;

  // Already logged in -> resume the funnel where it left off
  if (seller?.sellerId) {
    return <Navigate to={getNextRoute(seller) || "/dashboard"} replace />;
  }

  return children;
};

// 💡 Layout Wrapper
const AppLayout = ({ children }) => (
  <div className="app-layout">
    <div className="app-content">{children}</div>
  </div>
);

// 🔄 AppContent handles routing
const AppContent = () => {
  const { seller, loading } = useSeller();

  useEffect(() => {
    console.log("[Seller App] Auth State:", { loading, seller });
  }, [loading, seller]);

  if (loading) return <Splash />;

  // Never send a seller straight to the dashboard: respect pending steps
  const homeRoute = getNextRoute(seller) || "/dashboard";

  return (
    <Routes>
      {/* Root redirect */}
      <Route path="/" element={<Navigate to={homeRoute} replace />} />

      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />
      <Route
        path="/forgetpassword"
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        }
      />

      {/* Protected Registration Steps */}
      <Route
        path="/sellerregistration"
        element={
          <RegistrationRoute>
            <SellerRegistration />
          </RegistrationRoute>
        }
      />
      <Route
        path="/segmentregistration"
        element={
          <RegistrationRoute requires={["sellerRegistration"]}>
            <SegmentRegistration />
          </RegistrationRoute>
        }
      />
      <Route
        path="/productregistration"
        element={
          <RegistrationRoute
            requires={["sellerRegistration", "segmentRegistration"]}
          >
            <ProductRegistration />
          </RegistrationRoute>
        }
      />
      <Route
        path="/sellerprofile"
        element={
          <ProtectedRoute>
            <SellerProfile />
          </ProtectedRoute>
        }
      />

      {/* Protected Seller Functional Pages */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/uploadproduct"
        element={
          <ProtectedRoute>
            <UploadProduct />
          </ProtectedRoute>
        }
      />
      <Route
        path="/myregisteredproducts"
        element={
          <ProtectedRoute>
            <MyRegisteredProducts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/readystockservices"
        element={
          <ProtectedRoute>
            <ReadyStockServices />
          </ProtectedRoute>
        }
      />
      <Route
        path="/qcapprovalpage"
        element={
          <ProtectedRoute>
            <QCApprovalPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/orderserve"
        element={
          <ProtectedRoute>
            <OrderServe />
          </ProtectedRoute>
        }
      />
      <Route
        path="/openmarket"
        element={
          <ProtectedRoute>
            <Openmarket />
          </ProtectedRoute>
        }
      />
      <Route
        path="/preparation"
        element={
          <ProtectedRoute>
            <Preparation />
          </ProtectedRoute>
        }
      />
      <Route
        path="/processing"
        element={
          <ProtectedRoute>
            <Processing />
          </ProtectedRoute>
        }
      />
      <Route
        path="/liverates"
        element={
          <ProtectedRoute>
            <LiveRatesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/product/:productId"
        element={
          <ProtectedRoute>
            <ProductDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/mycatalogue"
        element={
          <ProtectedRoute>
            <MyCatalogue />
          </ProtectedRoute>
        }
      />
      <Route
        path="/catalogue/:type"
        element={
          <ProtectedRoute>
            <Catalogue />
          </ProtectedRoute>
        }
      />
      <Route
        path="/buyerrequest"
        element={
          <ProtectedRoute>
            <BuyerRequest />
          </ProtectedRoute>
        }
      />
      <Route
        path="/assortment"
        element={
          <ProtectedRoute>
            <Assortment />
          </ProtectedRoute>
        }
      />
      <Route
        path="/finalcorrection"
        element={
          <ProtectedRoute>
            <FinalCorrection />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rtd"
        element={
          <ProtectedRoute>
            <ReadyToDispatch />
          </ProtectedRoute>
        }
      />
      <Route
        path="/Assigned"
        element={
          <ProtectedRoute>
            <Pickup />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment"
        element={
          <ProtectedRoute>
            <Payment />
          </ProtectedRoute>
        }
      />
      <Route
        path="/orderhistory"
        element={
          <ProtectedRoute>
            <SellerOrderHistory />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rewards"
        element={
          <ProtectedRoute>
            <SellerRewards />
          </ProtectedRoute>
        }
      />
      <Route
        path="/debug"
        element={
          <ProtectedRoute>
            <DashboardDebug />
          </ProtectedRoute>
        }
      />

      {/* Fallback */}
      <Route path="*" element={<Navigate to={homeRoute} replace />} />
    </Routes>
  );
};

// 🌍 Main App Entry
function App() {
  React.useEffect(() => {
    NavigationBarManager.initialize();
  }, []);

  return (
    <ThemeProvider>
      <AuthProvider>
        <SellerProvider>
          <SafeAreaProvider>
            <Router>
              <AppLayout>
                <AppContent />
              </AppLayout>
            </Router>
          </SafeAreaProvider>
        </SellerProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
